import React, { useState } from 'react';
import PopOver from '../../components/deprecated/PopOver';
import { useGetUser } from '../../user/useAllUsers';
import useNavigate from '../../utils/router/useNavigate';
import userPath from '../../utils/url/userPath';
import MagneticWallItem from '../MagneticWallItem';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import { useMagneticWallLayout } from '../MagneticWallLayout';
import type { VersionChange } from './calculateVersionChanges';
import VersionChangeCard from './VersionChangeCard';
import { VersionChangesAuthorCardMenu } from './VersionChangesWallAuthorCard';

export type EditVersionChange = VersionChange & {
  type: 'edit';
};

interface EditVersionChangeCardProps {
  isAtCurrentHistory: (version: number) => boolean;
  versionChange: EditVersionChange;
  focused: boolean;
  onClick: () => void;
  historyInsertedAt: Date;
  goToVersion: () => void;
  initialIsPopOverOpen?: boolean;
}

const EditVersionChangeCard = ({
  isAtCurrentHistory,
  versionChange,
  focused,
  onClick,
  historyInsertedAt,
  goToVersion,
  initialIsPopOverOpen = false,
}: EditVersionChangeCardProps) => {
  const [isPopOverOpen, setIsPopOverOpen] = useState(initialIsPopOverOpen);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const { magneticWallItemSize } = useMagneticWallLayout();
  const getUser = useGetUser();
  const navigate = useNavigate();

  const author = getUser({ id: versionChange.authorId });
  if (!author) return null;

  const overflowMenu = (
    <VersionChangesAuthorCardMenu
      isAtCurrentVersion={isAtCurrentHistory(versionChange.version)}
      onGoToVersion={goToVersion}
      onOpenProfile={() => {
        navigate(userPath(author));
      }}
      onOpenChange={setIsOverflowMenuOpen}
      open={isOverflowMenuOpen}
    />
  );

  return (
    <MagneticWallItem.Root
      size={magneticWallItemSize}
      focused={focused}
      id={versionChange.id}
      onClick={onClick}
      compactView={
        <PopOver
          placement="left"
          trigger="click"
          arrow={<PopOver.Container.Arrow small />}
          modifiers={[
            { name: 'offset', options: { offset: [10, 8] } },
            { name: 'preventOverflow', enabled: false },
          ]}
          open={isPopOverOpen}
          onOpenChange={setIsPopOverOpen}
          preventCloseOnContentClick
          popOver={
            <VersionChangeCard.PopOverRoot>
              <VersionChangeCard.Content
                name={author.name}
                insertedAt={historyInsertedAt}
              />
              <VersionChangeCard.MenuContainer open={isOverflowMenuOpen}>
                {overflowMenu}
              </VersionChangeCard.MenuContainer>
            </VersionChangeCard.PopOverRoot>
          }
        >
          <VersionChangeCard.Root focused={focused} muted={!focused}>
            <VersionChangeCard.NewBadgeContainer showBadge>
              <VersionChangeCard.CompactAvatarContainer>
                <MagneticWallItemAvatar user={author} />
              </VersionChangeCard.CompactAvatarContainer>
            </VersionChangeCard.NewBadgeContainer>
          </VersionChangeCard.Root>
        </PopOver>
      }
      fullView={
        <VersionChangeCard.Root focused={focused} muted={!focused}>
          <MagneticWallItem.Card focused={focused} overlapped={false}>
            <VersionChangeCard.ContentContainer>
              <VersionChangeCard.NewBadgeContainer showBadge>
                <MagneticWallItemAvatar user={author} />
              </VersionChangeCard.NewBadgeContainer>
              <VersionChangeCard.Content
                name={author.name}
                insertedAt={historyInsertedAt}
              />
              <VersionChangeCard.MenuContainer open={isOverflowMenuOpen}>
                {overflowMenu}
              </VersionChangeCard.MenuContainer>
            </VersionChangeCard.ContentContainer>
          </MagneticWallItem.Card>
        </VersionChangeCard.Root>
      }
    />
  );
};

export default EditVersionChangeCard;
